import { type ComponentType, memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { type BaseFilterCardProps, FilterCard } from '~/components/Filters/FilterCard';
import { type FilterType } from '~/components/FiltersMenu/types';
import { type BaseStringSelectProps } from '~/components/StringSelect';
import {
  clearStringArrayFilterValuesAction,
  removeFilterAction,
  setStringArrayFilterValuesAction,
} from '~/store/filters/actions';
import { selectedStringArrayFiltersSelector } from '~/store/filters/selectors';
import { type SelectedFilter, type StringArrayFilterType } from '~/store/filters/types';
import { useAirSelector } from '~/utils/ReduxUtils';

export type StringArrayFilterCardProps = {
  StringSelect: ComponentType<BaseStringSelectProps>;
  filterCardType: FilterType;
  filter: StringArrayFilterType;
} & BaseFilterCardProps;

export const StringArrayFilterCard = memo(
  ({ StringSelect, filter, filterCardType, ...props }: StringArrayFilterCardProps) => {
    const dispatch = useDispatch();

    const clearValues = useCallback(() => {
      dispatch(clearStringArrayFilterValuesAction({ filter }));
    }, [dispatch, filter]);

    const selectedValues = useAirSelector((st) => selectedStringArrayFiltersSelector(st, filter));

    const onSelectedValuesChange = useCallback(
      (values: string[]) => dispatch(setStringArrayFilterValuesAction({ values, filter })),
      [dispatch, filter],
    );

    const onRemoveFilter = (filter: SelectedFilter) => {
      dispatch(removeFilterAction({ filter }));
      clearValues();
    };

    return (
      <FilterCard currentFilter={filterCardType} onClearClick={clearValues} onRemoveFilter={onRemoveFilter} {...props}>
        <StringSelect selectedValues={selectedValues} onSelectionChange={onSelectedValuesChange} />
      </FilterCard>
    );
  },
);

StringArrayFilterCard.displayName = 'StringArrayFilterCard';
